import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { normalizarTexto } from '../../utils/texto';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings, Users, Tag, GitBranch, Sliders, Archive, ArchiveRestore,
  Loader2, Check, X, AlertTriangle, Plus, Trash2, Search, Save, ShieldCheck, AlertOctagon,
  Diamond, Calendar, Pencil, RotateCcw, Globe,
} from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { iniciales } from './utils/helpers';
import { COLORES, CATEGORIAS } from './utils/constants';
import { FechaText, HITO_COLOR_DEFAULT, hitoEstado } from './utils/displays';
import ConfirmDialog from './ConfirmDialog';

// =================================================================
// Tab: GENERAL
// =================================================================

const TabGeneral = ({ proyecto, isOwner, onChanged }) => {
  const navigate = useNavigate();
  const [nombre, setNombre] = useState(proyecto.nombre);
  const [descripcion, setDescripcion] = useState(proyecto.descripcion || '');
  const [color, setColor] = useState(proyecto.color || '#06b6d4');
  const [esContinuo, setEsContinuo] = useState(!!proyecto.es_continuo);
  const [slug, setSlug] = useState(proyecto.slug);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmArchivar, setConfirmArchivar] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  const dirty = nombre !== proyecto.nombre
    || descripcion !== (proyecto.descripcion || '')
    || color !== proyecto.color
    || esContinuo !== !!proyecto.es_continuo
    || slug !== proyecto.slug;

  const slugValido = /^[A-Z0-9]{2,12}$/.test(slug) && !/^[0-9]+$/.test(slug);

  const save = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const { data } = await axiosInstance.put(`/api/proyectos/${proyecto.id}`, {
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || null,
        color,
        es_continuo: esContinuo,
        ...(slug !== proyecto.slug ? { slug } : {}),
      });
      // El codigo va en la URL: al cambiarlo hay que ir a la nueva (el shell
      // recarga el proyecto al cambiar el slug).
      if (data?.slug && data.slug !== proyecto.slug) {
        navigate(`/proyectos/${data.slug}/configuracion`, { replace: true });
      } else {
        onChanged?.();
      }
    } catch (err) {
      setSaveError(err.response?.data?.error || 'No se pudieron guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchivar = async () => {
    try {
      if (proyecto.archivado) {
        await axiosInstance.post(`/api/proyectos/${proyecto.id}/desarchivar`);
      } else {
        await axiosInstance.post(`/api/proyectos/${proyecto.id}/archivar`);
      }
      onChanged?.();
      if (!proyecto.archivado) navigate('/proyectos');
    } catch (err) {
      console.error('Error toggle archivar:', err);
    }
  };

  const handleEliminar = async () => {
    setConfirmEliminar(false);
    setEliminando(true);
    try {
      await axiosInstance.delete(`/api/proyectos/${proyecto.id}`);
      navigate('/proyectos');
    } catch (err) {
      console.error('Error eliminando proyecto:', err);
      setEliminando(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-base font-semibold text-slate-900 tracking-tight mb-1">Información general</h2>
        <p className="text-xs text-slate-500">Datos básicos del proyecto. Visibles para todos los miembros.</p>
      </div>

      {/* Nombre */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Nombre</label>
        <input
          type="text"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          disabled={!isOwner}
          maxLength={120}
          className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 disabled:bg-slate-50 disabled:text-slate-500"
        />
      </div>

      {/* Codigo (slug): prefijo de los codigos de tarea. Solo el lider lo cambia. */}
      <div>
        <label htmlFor="cfg-slug" className="block text-xs font-medium text-slate-600 mb-1.5">
          Código del proyecto
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-md ${
            isOwner ? 'bg-white border-slate-300 focus-within:ring-2 focus-within:ring-cyan-500/20 focus-within:border-cyan-500' : 'bg-slate-50 border-slate-200'
          }`}>
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <input
              id="cfg-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12))}
              disabled={!isOwner}
              size={Math.max(slug.length, 4)}
              aria-describedby="cfg-slug-hint"
              className="font-mono text-xs tracking-widest text-slate-700 uppercase bg-transparent focus:outline-none disabled:text-slate-700"
            />
          </div>
          <span id="cfg-slug-hint" className={`text-[11px] ${slug && !slugValido ? 'text-rose-600' : 'text-slate-400'}`}>
            {slug && !slugValido
              ? 'De 2 a 12 letras o números.'
              : `Las tareas se ven como ${slug || proyecto.slug}-1, ${slug || proyecto.slug}-2...`}
          </span>
        </div>
        {isOwner && slug !== proyecto.slug && slugValido && (
          <p className="mt-1.5 text-[11px] text-amber-700">
            Al guardar cambian los códigos de todas las tareas y el enlace del proyecto. Las menciones escritas
            con el código anterior en comentarios dejan de enlazar.
          </p>
        )}
      </div>

      {/* Descripcion */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Descripción</label>
        <textarea
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          rows={3}
          disabled={!isOwner}
          placeholder="¿De qué trata este proyecto?"
          className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 disabled:bg-slate-50 resize-none"
        />
      </div>

      {/* Color */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-2">Color de identidad</label>
        <div className="flex items-center gap-2">
          {COLORES.map((c) => (
            <button
              key={c.hex}
              type="button"
              disabled={!isOwner}
              onClick={() => setColor(c.hex)}
              title={c.nombre}
              className="relative w-8 h-8 rounded-full transition-transform hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: c.hex }}
            >
              {color === c.hex && (
                <motion.span
                  layoutId="settings-color-ring"
                  className="absolute inset-0 rounded-full ring-2 ring-offset-2 ring-slate-900"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tipo: proyecto continuo */}
      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer group select-none">
          <input
            type="checkbox"
            checked={esContinuo}
            onChange={(e) => setEsContinuo(e.target.checked)}
            disabled={!isOwner}
            className="h-4 w-4 rounded border-slate-300 accent-cyan-600 cursor-pointer disabled:cursor-not-allowed"
          />
          <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">
            Proyecto continuo
          </span>
        </label>
      </div>

      {/* Save bar */}
      {isOwner && (
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200/60">
          {dirty && (
            <button
              onClick={() => {
                setNombre(proyecto.nombre);
                setDescripcion(proyecto.descripcion || '');
                setColor(proyecto.color || '#06b6d4');
                setEsContinuo(!!proyecto.es_continuo);
                setSlug(proyecto.slug);
                setSaveError('');
              }}
              className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
            >
              Descartar
            </button>
          )}
          {saveError && <span className="mr-auto text-xs text-rose-600">{saveError}</span>}
          <button
            onClick={save}
            disabled={!dirty || saving || !slugValido}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Guardar cambios
          </button>
        </div>
      )}

      {/* Danger zone */}
      {isOwner && (
        <div className="mt-12 pt-6 border-t border-slate-200/60">
          <h3 className="text-xs font-semibold text-slate-500 tracking-widest uppercase mb-3">Zona delicada</h3>

          {/* Archivar / desarchivar */}
          <div className="bg-rose-50/50 border border-rose-200/70 rounded-xl p-4 flex items-start justify-between gap-4 mb-3">
            <div>
              <h4 className="text-sm font-medium text-slate-900">
                {proyecto.archivado ? 'Desarchivar proyecto' : 'Archivar proyecto'}
              </h4>
              <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                {proyecto.archivado
                  ? 'El proyecto vuelve a aparecer en la lista activa y sus tareas reaparecen en Mis tareas, Calendario y Gantt.'
                  : 'El proyecto se oculta de la lista activa y sus tareas dejan de aparecer en Mis tareas, Calendario y Gantt. Lo puedes desarchivar luego.'}
              </p>
            </div>
            {confirmArchivar ? (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => setConfirmArchivar(false)}
                  className="px-2 py-1.5 text-xs text-slate-600 hover:text-slate-900 rounded transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleArchivar}
                  className={`px-3 py-1.5 text-xs font-medium text-white rounded transition-colors ${
                    proyecto.archivado ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-rose-600 hover:bg-rose-700'
                  }`}
                >
                  Confirmar
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmArchivar(true)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                  proyecto.archivado
                    ? 'text-cyan-700 bg-white border border-cyan-200 hover:bg-cyan-50'
                    : 'text-rose-700 bg-white border border-rose-200 hover:bg-rose-50'
                }`}
              >
                {proyecto.archivado ? <ArchiveRestore size={13} /> : <Archive size={13} />}
                {proyecto.archivado ? 'Desarchivar' : 'Archivar'}
              </button>
            )}
          </div>

          {/* Eliminar (hard delete) */}
          <div className="bg-rose-50/50 border border-rose-200/70 rounded-xl p-4 flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
                <AlertOctagon size={13} className="text-rose-600" strokeWidth={2.2} />
                Eliminar proyecto
              </h4>
              <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                Borra el proyecto, todas sus tareas, comentarios, adjuntos y dependencias.
                Acción <strong className="text-rose-700">irreversible</strong>. Si solo quieres ocultarlo, archívalo.
              </p>
            </div>
            <button
              onClick={() => setConfirmEliminar(true)}
              disabled={eliminando}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-md transition-colors flex-shrink-0 disabled:opacity-50"
            >
              {eliminando ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              {eliminando ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmEliminar}
        title={`¿Eliminar "${proyecto.nombre}"?`}
        message="Se borrarán todas las tareas, comentarios, adjuntos y dependencias del proyecto. Esta acción no se puede deshacer. Si solo quieres ocultarlo, usa Archivar."
        variant="danger"
        confirmLabel="Eliminar definitivamente"
        onConfirm={handleEliminar}
        onClose={() => setConfirmEliminar(false)}
      />
    </div>
  );
};

// =================================================================
// Tab: MIEMBROS
// =================================================================

const TabMiembros = ({ proyecto, isOwner, onChanged, userEmpleadoId }) => {
  const [adding, setAdding] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [empleados, setEmpleados] = useState([]);
  const [loadingEmpleados, setLoadingEmpleados] = useState(false);
  const [actioning, setActioning] = useState(null);
  const [miembroAQuitar, setMiembroAQuitar] = useState(null);
  // Cambio de rol pide confirmacion: dar/quitar Owner cambia permisos criticos
  // (puede dejar al proyecto sin owner si te lo quitas a vos mismo).
  const [rolPendiente, setRolPendiente] = useState(null); // miembro

  useEffect(() => {
    if (!adding) return;
    setLoadingEmpleados(true);
    axiosInstance.get('/api/empleados')
      .then(({ data }) => setEmpleados(data || []))
      .catch(() => setEmpleados([]))
      .finally(() => setLoadingEmpleados(false));
  }, [adding]);

  const miembros = proyecto.miembros || [];
  const miembrosIds = new Set(miembros.map((m) => m.empleado_id));

  const candidatos = useMemo(() => {
    const q = normalizarTexto(busqueda).trim();
    return empleados
      .filter((e) => !miembrosIds.has(e.id))
      .filter((e) => !q || normalizarTexto(e.nombre || e.nombre_completo).includes(q))
      .slice(0, 30);
  }, [empleados, miembrosIds, busqueda]);

  const handleAgregar = async (empleado) => {
    setActioning(`add-${empleado.id}`);
    try {
      await axiosInstance.post(`/api/proyectos/${proyecto.id}/miembros`, {
        empleado_id: empleado.id,
        rol: 'miembro',
      });
      onChanged?.();
      setBusqueda('');
    } catch (err) {
      console.error('Error agregando miembro:', err);
    } finally {
      setActioning(null);
    }
  };

  const doQuitar = async () => {
    if (!miembroAQuitar) return;
    const m = miembroAQuitar;
    setMiembroAQuitar(null);
    setActioning(`rm-${m.empleado_id}`);
    try {
      await axiosInstance.delete(`/api/proyectos/${proyecto.id}/miembros/${m.empleado_id}`);
      onChanged?.();
    } catch (err) {
      console.error('Error quitando miembro:', err);
    } finally {
      setActioning(null);
    }
  };

  const confirmarRol = async () => {
    if (!rolPendiente) return;
    const m = rolPendiente;
    setRolPendiente(null);
    const nuevoRol = m.rol === 'owner' ? 'miembro' : 'owner';
    setActioning(`role-${m.empleado_id}`);
    try {
      await axiosInstance.patch(`/api/proyectos/${proyecto.id}/miembros/${m.empleado_id}`, {
        rol: nuevoRol,
      });
      onChanged?.();
    } catch (err) {
      console.error('Error cambiando rol:', err);
    } finally {
      setActioning(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900 tracking-tight mb-1">Miembros</h2>
          <p className="text-xs text-slate-500">
            Solo los miembros pueden ver las tareas del proyecto. Cada miembro mueve y cierra
            las tareas que tiene asignadas; los líderes controlan el tablero y pueden mover
            cualquier tarea.
          </p>
        </div>
        <span className="text-xs text-slate-400 tabular-nums">
          {miembros.length} {miembros.length === 1 ? 'miembro' : 'miembros'}
        </span>
      </div>

      {/* Lista */}
      <ul className="bg-white border border-slate-200/70 rounded-xl divide-y divide-slate-100 overflow-hidden">
        {miembros.map((m) => {
          const yo = m.empleado_id === userEmpleadoId;
          const isOwnerMiembro = m.rol === 'owner';
          return (
            <li key={m.empleado_id} className="flex items-center gap-3 px-4 py-3">
              <span
                className="w-9 h-9 rounded-full text-xs font-semibold flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${proyecto.color || '#06b6d4'}1f`, color: proyecto.color || '#06b6d4' }}
              >
                {iniciales(m.nombre)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {m.nombre}
                  {yo && <span className="ml-1.5 text-[10px] font-normal text-slate-400">(tú)</span>}
                </p>
                <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                  {isOwnerMiembro && <ShieldCheck size={10} className="text-amber-500" strokeWidth={2.5} />}
                  {isOwnerMiembro ? 'Líder' : 'Miembro'}
                </p>
              </div>
              {isOwner && !yo && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setRolPendiente(m)}
                    disabled={actioning === `role-${m.empleado_id}`}
                    className="text-[11px] px-2 py-1 text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded transition-colors disabled:opacity-50"
                  >
                    {isOwnerMiembro ? 'Quitar líder' : 'Hacer líder'}
                  </button>
                  <button
                    onClick={() => setMiembroAQuitar(m)}
                    disabled={actioning === `rm-${m.empleado_id}`}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors disabled:opacity-50"
                    title="Quitar del proyecto"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Agregar */}
      {isOwner && (
        <div>
          {!adding ? (
            <button
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 rounded-md transition-colors"
            >
              <Plus size={13} strokeWidth={2.5} />
              Agregar miembro
            </button>
          ) : (
            <div className="bg-white border border-slate-200/70 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-600">Agregar miembros</span>
                <button
                  onClick={() => { setAdding(false); setBusqueda(''); }}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="relative mb-2">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  autoFocus
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar empleado..."
                  className="w-full pl-7 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-md placeholder-slate-400 focus:outline-none focus:bg-white focus:border-slate-300"
                />
              </div>
              <div className="max-h-60 overflow-y-auto -mx-1">
                {loadingEmpleados ? (
                  <div className="text-center py-4">
                    <Loader2 size={14} className="text-slate-400 animate-spin inline" />
                  </div>
                ) : candidatos.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-3">Sin resultados</p>
                ) : (
                  candidatos.map((e) => (
                    <button
                      key={e.id}
                      onClick={() => handleAgregar(e)}
                      disabled={actioning === `add-${e.id}`}
                      className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm hover:bg-slate-50 rounded transition-colors disabled:opacity-50"
                    >
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold flex items-center justify-center">
                        {iniciales(e.nombre || e.nombre_completo)}
                      </span>
                      <span className="text-slate-800 truncate flex-1">{e.nombre || e.nombre_completo}</span>
                      {actioning === `add-${e.id}` ? (
                        <Loader2 size={12} className="text-slate-400 animate-spin" />
                      ) : (
                        <Plus size={12} className="text-slate-400" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!miembroAQuitar}
        title="¿Quitar a este miembro?"
        message={miembroAQuitar ? `${miembroAQuitar.nombre} dejará de ver y editar las tareas del proyecto. Puedes volver a agregarlo cuando quieras.` : ''}
        variant="warning"
        confirmLabel="Quitar"
        onConfirm={doQuitar}
        onClose={() => setMiembroAQuitar(null)}
      />

      <ConfirmDialog
        isOpen={!!rolPendiente}
        title={rolPendiente?.rol === 'owner' ? '¿Quitar el rol de líder?' : '¿Hacer líder a este miembro?'}
        message={rolPendiente
          ? (rolPendiente.rol === 'owner'
              ? `${rolPendiente.nombre} pasará a ser miembro: solo podrá mover sus propias tareas y dejará de poder editar el proyecto, gestionar miembros o archivarlo.`
              : `${rolPendiente.nombre} pasará a ser líder: podrá mover cualquier tarea del tablero, editar el proyecto, gestionar miembros y archivarlo.`)
          : ''}
        variant="warning"
        confirmLabel="Cambiar rol"
        onConfirm={confirmarRol}
        onClose={() => setRolPendiente(null)}
      />
    </div>
  );
};

// =================================================================
// Tab: ESTADOS
// =================================================================

const TabEstados = ({ proyecto, isOwner, onChanged }) => {
  const [creating, setCreating] = useState(null); // categoria key
  const [draft, setDraft] = useState({ nombre: '', color: '' });
  const [editing, setEditing] = useState(null); // estado id

  const estadosPorCategoria = useMemo(() => {
    const map = {};
    CATEGORIAS.forEach((c) => { map[c.key] = []; });
    (proyecto.estados || []).forEach((e) => {
      if (map[e.categoria]) map[e.categoria].push(e);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => a.orden - b.orden));
    return map;
  }, [proyecto.estados]);

  const handleCreate = async (categoria) => {
    const t = draft.nombre.trim();
    if (!t) { setCreating(null); return; }
    try {
      await axiosInstance.post(`/api/proyectos/${proyecto.id}/estados`, {
        categoria,
        nombre: t,
        color: draft.color || null,
      });
      setDraft({ nombre: '', color: '' });
      setCreating(null);
      onChanged?.();
    } catch (err) {
      console.error('Error creando estado:', err);
    }
  };

  // Confirmación con ConfirmDialog estilizado en vez de window.confirm.
  // pendingDelete guarda el estado a eliminar mientras está abierto el diálogo.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const handleDelete = (estado) => setPendingDelete(estado);

  const confirmDelete = async () => {
    const estado = pendingDelete;
    if (!estado) return;
    try {
      await axiosInstance.delete(`/api/proyectos/${proyecto.id}/estados/${estado.id}`);
      setPendingDelete(null);
      onChanged?.();
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'No se pudo eliminar.');
      setPendingDelete(null);
    }
  };

  const handleRename = async (estado, nuevoNombre) => {
    if (!nuevoNombre.trim() || nuevoNombre === estado.nombre) {
      setEditing(null);
      return;
    }
    try {
      await axiosInstance.put(`/api/proyectos/${proyecto.id}/estados/${estado.id}`, {
        nombre: nuevoNombre.trim(),
      });
      setEditing(null);
      onChanged?.();
    } catch (err) {
      console.error('Error editando estado:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-slate-900 tracking-tight mb-1">Estados del board</h2>
        <p className="text-xs text-slate-500 max-w-2xl">
          Las 5 categorías son fijas. Dentro de cada una puedes definir sub-estados
          custom para tu flujo (ej: "Diseño" y "Revisión" dentro de "En curso").
        </p>
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Eliminar sub-estado"
        message={pendingDelete && `¿Eliminar "${pendingDelete.nombre}"? Las tareas con este estado se moverán a otro de la misma categoría.`}
        variant="danger"
        confirmLabel="Eliminar"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
      <ConfirmDialog
        isOpen={!!deleteError}
        title="No se pudo eliminar"
        message={deleteError}
        variant="warning"
        confirmLabel="Entendido"
        cancelLabel="Cerrar"
        onConfirm={() => setDeleteError(null)}
        onClose={() => setDeleteError(null)}
      />

      <div className="space-y-3">
        {CATEGORIAS.map((cat) => {
          const estados = estadosPorCategoria[cat.key] || [];
          return (
            <div key={cat.key} className="bg-white border border-slate-200/70 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50/50 border-b border-slate-100">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat.color }} />
                <span className="text-[11px] font-bold tracking-[0.1em] uppercase text-slate-600">
                  {cat.label}
                </span>
                <span className="text-[10px] font-medium text-slate-400 tabular-nums">
                  {estados.length}
                </span>
              </div>
              <ul className="divide-y divide-slate-100">
                {estados.map((e) => (
                  <li key={e.id} className="group flex items-center gap-2 px-4 py-2 hover:bg-slate-50 transition-colors">
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: e.color || cat.color }}
                    />
                    {editing === e.id ? (
                      <input
                        autoFocus
                        defaultValue={e.nombre}
                        onBlur={(ev) => handleRename(e, ev.target.value)}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter') { ev.preventDefault(); ev.target.blur(); }
                          if (ev.key === 'Escape') setEditing(null);
                        }}
                        className="flex-1 text-sm bg-transparent border-0 focus:outline-none text-slate-800 leading-snug px-0"
                      />
                    ) : (
                      <span
                        onClick={() => isOwner && setEditing(e.id)}
                        className={`flex-1 text-sm text-slate-700 leading-snug ${isOwner ? 'cursor-text' : ''}`}
                      >
                        {e.nombre}
                      </span>
                    )}
                    {isOwner && estados.length > 1 && (
                      <button
                        onClick={() => handleDelete(e)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-600 transition-opacity"
                        title="Eliminar sub-estado"
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </li>
                ))}
                {isOwner && (
                  <li className="px-4 py-2">
                    {creating === cat.key ? (
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                        <input
                          autoFocus
                          value={draft.nombre}
                          onChange={(e) => setDraft({ ...draft, nombre: e.target.value })}
                          onBlur={() => handleCreate(cat.key)}
                          onKeyDown={(ev) => {
                            if (ev.key === 'Enter') { ev.preventDefault(); handleCreate(cat.key); }
                            if (ev.key === 'Escape') { setCreating(null); setDraft({ nombre: '', color: '' }); }
                          }}
                          placeholder="Nombre del sub-estado..."
                          className="flex-1 text-sm bg-transparent border-0 focus:outline-none placeholder-slate-400 text-slate-800"
                        />
                      </div>
                    ) : (
                      <button
                        onClick={() => { setCreating(cat.key); setDraft({ nombre: '', color: cat.color }); }}
                        className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 transition-colors"
                      >
                        <Plus size={11} strokeWidth={2.5} />
                        Añadir sub-estado
                      </button>
                    )}
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =================================================================
// Tab: ETIQUETAS
// =================================================================

const TabEtiquetas = ({ proyecto, isOwner, onChanged, userEmpleadoId }) => {
  const [adding, setAdding] = useState(false);
  const [nombre, setNombre] = useState('');
  const [color, setColor] = useState('#94a3b8');
  // Las globales (personales) las puede crear cualquiera; las de proyecto, solo
  // el owner. Por eso un no-owner solo puede crear globales (toggle fijo).
  const [esGlobal, setEsGlobal] = useState(!isOwner);

  const startAdding = () => {
    setEsGlobal(!isOwner);
    setAdding(true);
  };

  const handleCreate = async () => {
    const t = nombre.trim();
    if (!t) { setAdding(false); return; }
    try {
      await axiosInstance.post(`/api/proyectos/${proyecto.id}/etiquetas`, {
        nombre: t, color, es_global: esGlobal,
      });
      setNombre('');
      setColor('#94a3b8');
      setAdding(false);
      onChanged?.();
    } catch (err) {
      console.error('Error creando etiqueta:', err);
    }
  };

  const [pendingDelete, setPendingDelete] = useState(null);

  const handleDelete = (et) => setPendingDelete(et);

  const confirmDelete = async () => {
    const et = pendingDelete;
    if (!et) return;
    try {
      await axiosInstance.delete(`/api/proyectos/${proyecto.id}/etiquetas/${et.id}`);
      setPendingDelete(null);
      onChanged?.();
    } catch (err) {
      console.error('Error eliminando etiqueta:', err);
      setPendingDelete(null);
    }
  };

  const etiquetas = proyecto.etiquetas || [];
  // Puedo borrar una etiqueta si: es de proyecto y soy owner, o es global y mía.
  const puedeBorrar = (e) =>
    e.es_global ? e.owner_empleado_id === userEmpleadoId : isOwner;

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900 tracking-tight mb-1">Etiquetas</h2>
          <p className="text-xs text-slate-500">
            Para clasificar tareas. Las <span className="inline-flex items-center gap-0.5"><Globe size={11} className="text-slate-400" strokeWidth={2} /> globales</span> son tuyas y aparecen en todos tus proyectos.
          </p>
        </div>
        <span className="text-xs text-slate-400 tabular-nums">{etiquetas.length}</span>
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Eliminar etiqueta"
        message={pendingDelete && (
          pendingDelete.es_global
            ? `¿Eliminar "${pendingDelete.nombre}"? Es una etiqueta global tuya: se quitará de las tareas de todos tus proyectos.`
            : `¿Eliminar "${pendingDelete.nombre}"? Se quitará de las tareas que la tengan.`
        )}
        variant="danger"
        confirmLabel="Eliminar"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      {etiquetas.length === 0 && !adding ? (
        <div className="text-center py-10 bg-slate-50/50 border border-dashed border-slate-200 rounded-xl">
          <Tag size={22} className="text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-slate-500 mb-3">Aún no hay etiquetas.</p>
          <button
            onClick={startAdding}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-700 bg-white hover:bg-slate-50 rounded-md transition-colors border border-slate-200"
          >
            <Plus size={13} strokeWidth={2.5} />
            Crear primera etiqueta
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {etiquetas.map((e) => (
            <div
              key={e.id}
              className="group inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-md text-xs"
              style={{
                backgroundColor: `${e.color || '#94a3b8'}1f`,
                color: e.color || '#475569',
              }}
            >
              {e.es_global ? (
                <Globe size={11} strokeWidth={2.2} className="opacity-80" />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color || '#94a3b8' }} />
              )}
              <span className="font-medium">{e.nombre}</span>
              {puedeBorrar(e) && (
                <button
                  onClick={() => handleDelete(e)}
                  className="opacity-0 group-hover:opacity-100 ml-0.5 p-0.5 rounded hover:bg-black/10 transition-all"
                  title="Eliminar etiqueta"
                >
                  <X size={10} />
                </button>
              )}
            </div>
          ))}

          {!adding && (
            <button
              onClick={startAdding}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
            >
              <Plus size={11} strokeWidth={2.5} />
              Nueva etiqueta
            </button>
          )}

          {adding && (
            <div className="inline-flex items-center gap-2 pl-2 pr-1.5 py-1 rounded-md ring-2 ring-cyan-500/30" style={{ backgroundColor: `${color}1f` }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
              <input
                autoFocus
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleCreate(); }
                  if (e.key === 'Escape') { setAdding(false); setNombre(''); }
                }}
                placeholder="Nombre..."
                maxLength={120}
                className="text-xs bg-transparent border-0 focus:outline-none placeholder-slate-400 w-24"
                style={{ color: color }}
              />
              <div className="flex items-center gap-1">
                {COLORES.slice(0, 8).map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    onClick={() => setColor(c.hex)}
                    title={c.nombre}
                    className={`w-4 h-4 rounded-full transition-transform hover:scale-110 ${color === c.hex ? 'ring-2 ring-offset-1 ring-slate-900' : ''}`}
                    style={{ backgroundColor: c.hex }}
                  />
                ))}
              </div>
              {/* Toggle global. Para no-owner queda fijo en global (no pueden crear de proyecto). */}
              <button
                type="button"
                onClick={() => isOwner && setEsGlobal((v) => !v)}
                disabled={!isOwner}
                title={esGlobal ? 'Etiqueta global (en todos tus proyectos)' : 'Etiqueta solo de este proyecto'}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                  esGlobal ? 'bg-cyan-500 text-white' : 'bg-white/60 text-slate-500 hover:text-slate-700'
                } ${!isOwner ? 'cursor-default' : ''}`}
              >
                <Globe size={10} strokeWidth={2.5} />
                Global
              </button>
              <button
                onClick={handleCreate}
                className="p-0.5 text-slate-700 hover:bg-black/10 rounded"
                title="Crear"
              >
                <Check size={11} />
              </button>
              <button
                onClick={() => { setAdding(false); setNombre(''); }}
                className="p-0.5 text-slate-400 hover:bg-black/10 rounded"
                title="Cancelar"
              >
                <X size={11} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =================================================================
// Tab: HITOS
// =================================================================

// Formulario compartido para crear/editar un hito. En modo edicion recibe
// `hito` con los valores iniciales; en modo creacion arranca vacio.
const HitoForm = ({ hito, onSubmit, onCancel, submitting }) => {
  const [nombre, setNombre] = useState(hito?.nombre || '');
  const [fecha, setFecha] = useState(hito?.fecha || '');
  const [color, setColor] = useState(hito?.color || HITO_COLOR_DEFAULT);
  const [descripcion, setDescripcion] = useState(hito?.descripcion || '');

  const valido = nombre.trim() && fecha;

  const handleSubmit = () => {
    if (!valido) return;
    onSubmit({
      nombre: nombre.trim(),
      fecha,
      color,
      descripcion: descripcion.trim() || null,
    });
  };

  return (
    <div className="bg-white border border-slate-200/70 rounded-xl p-4 space-y-3">
      <div className="grid grid-cols-[1fr_auto] gap-3">
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Nombre</label>
          <input
            type="text"
            autoFocus
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); }
              if (e.key === 'Escape') onCancel();
            }}
            maxLength={120}
            placeholder="Ej: Entrega beta"
            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Fecha</label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="px-3 py-2 text-sm bg-white border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Color</label>
        <div className="flex items-center gap-1.5">
          {COLORES.map((c) => (
            <button
              key={c.hex}
              type="button"
              onClick={() => setColor(c.hex)}
              title={c.nombre}
              className={`w-5 h-5 rounded-full transition-transform hover:scale-110 ${color === c.hex ? 'ring-2 ring-offset-1 ring-slate-900' : ''}`}
              style={{ backgroundColor: c.hex }}
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-[11px] font-medium text-slate-500 mb-1">
          Descripción <span className="text-slate-400">(opcional)</span>
        </label>
        <textarea
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          rows={2}
          placeholder="¿Qué representa este hito?"
          className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 resize-none"
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
        >
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          disabled={!valido || submitting}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          {hito ? 'Guardar' : 'Crear hito'}
        </button>
      </div>
    </div>
  );
};

const TabHitos = ({ proyecto, isOwner, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // hito id
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(null); // hito id
  const [pendingDelete, setPendingDelete] = useState(null);

  // Ordenados por fecha asc; los sin fecha (no deberian existir, fecha es requerida) al final.
  const hitos = useMemo(() => {
    return [...(proyecto.hitos || [])].sort((a, b) => {
      if (!a.fecha) return 1;
      if (!b.fecha) return -1;
      return a.fecha.localeCompare(b.fecha);
    });
  }, [proyecto.hitos]);

  const handleCreate = async (payload) => {
    setSubmitting(true);
    try {
      await axiosInstance.post(`/api/proyectos/${proyecto.id}/hitos`, payload);
      setAdding(false);
      onChanged?.();
    } catch (err) {
      console.error('Error creando hito:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (hitoId, payload) => {
    setSubmitting(true);
    try {
      await axiosInstance.put(`/api/proyectos/${proyecto.id}/hitos/${hitoId}`, payload);
      setEditing(null);
      onChanged?.();
    } catch (err) {
      console.error('Error editando hito:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (hito) => {
    setToggling(hito.id);
    try {
      await axiosInstance.post(`/api/proyectos/${proyecto.id}/hitos/${hito.id}/completar`, {
        completado: !hito.completado,
      });
      onChanged?.();
    } catch (err) {
      console.error('Error cambiando estado del hito:', err);
    } finally {
      setToggling(null);
    }
  };

  const confirmDelete = async () => {
    const h = pendingDelete;
    if (!h) return;
    try {
      await axiosInstance.delete(`/api/proyectos/${proyecto.id}/hitos/${h.id}`);
      setPendingDelete(null);
      onChanged?.();
    } catch (err) {
      console.error('Error eliminando hito:', err);
      setPendingDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900 tracking-tight mb-1">Hitos</h2>
          <p className="text-xs text-slate-500 max-w-2xl">
            Fechas clave del proyecto. Asocia tareas a un hito para seguir su avance.
          </p>
        </div>
        <span className="text-xs text-slate-400 tabular-nums">{hitos.length}</span>
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Eliminar hito"
        message={pendingDelete && `¿Eliminar "${pendingDelete.nombre}"? Las tareas asociadas dejarán de tener un hito, pero no se borran.`}
        variant="danger"
        confirmLabel="Eliminar"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      {/* Lista */}
      {hitos.length === 0 && !adding ? (
        <div className="text-center py-10 bg-slate-50/50 border border-dashed border-slate-200 rounded-xl">
          <Diamond size={22} className="text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-slate-500 mb-3">Aún no hay hitos.</p>
          {isOwner && (
            <button
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-700 bg-white hover:bg-slate-50 rounded-md transition-colors border border-slate-200"
            >
              <Plus size={13} strokeWidth={2.5} />
              Crear primer hito
            </button>
          )}
        </div>
      ) : (
        <ul className="bg-white border border-slate-200/70 rounded-xl divide-y divide-slate-100 overflow-hidden">
          {hitos.map((h) => {
            if (editing === h.id) {
              return (
                <li key={h.id} className="p-3">
                  <HitoForm
                    hito={h}
                    submitting={submitting}
                    onSubmit={(payload) => handleEdit(h.id, payload)}
                    onCancel={() => setEditing(null)}
                  />
                </li>
              );
            }
            const { vencido } = hitoEstado(h);
            const dotColor = h.completado ? '#10b981' : (vencido ? '#e11d48' : (h.color || HITO_COLOR_DEFAULT));
            const total = h.tareas_total || 0;
            const hechas = h.tareas_hechas || 0;
            return (
              <li
                key={h.id}
                className="group flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
              >
                {/* Rombo del color del hito */}
                <span className="flex-shrink-0">
                  <Diamond
                    size={13}
                    strokeWidth={2.2}
                    style={{ color: dotColor, fill: h.completado ? 'currentColor' : 'none' }}
                  />
                </span>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${h.completado ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                    {h.nombre}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                      <Calendar size={10} strokeWidth={2} />
                      <FechaText fecha={h.fecha} />
                    </span>
                    {h.completado && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                        <Check size={10} strokeWidth={2.5} />
                        Cumplido
                      </span>
                    )}
                  </div>
                </div>

                {/* Contador de tareas */}
                <span
                  className="text-[11px] font-medium text-slate-500 tabular-nums flex-shrink-0"
                  title={`${hechas} de ${total} tareas completadas`}
                >
                  {hechas}/{total}
                </span>

                {/* Acciones (owner) */}
                {isOwner && (
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => handleToggle(h)}
                      disabled={toggling === h.id}
                      title={h.completado ? 'Marcar como pendiente' : 'Marcar como cumplido'}
                      className={`p-1.5 rounded transition-colors disabled:opacity-50 ${
                        h.completado
                          ? 'text-emerald-600 hover:bg-emerald-50'
                          : 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50'
                      }`}
                    >
                      {toggling === h.id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : h.completado ? (
                        <RotateCcw size={13} />
                      ) : (
                        <Check size={13} strokeWidth={2.5} />
                      )}
                    </button>
                    <button
                      onClick={() => setEditing(h.id)}
                      title="Editar hito"
                      className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-all"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => setPendingDelete(h)}
                      title="Eliminar hito"
                      className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-all"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Crear: form si esta activo, boton "Nuevo hito" si ya hay hitos (la
          lista vacia trae su propio boton "Crear primer hito"). */}
      {isOwner && adding && (
        <HitoForm
          submitting={submitting}
          onSubmit={handleCreate}
          onCancel={() => setAdding(false)}
        />
      )}
      {isOwner && !adding && hitos.length > 0 && (
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 rounded-md transition-colors"
        >
          <Plus size={13} strokeWidth={2.5} />
          Nuevo hito
        </button>
      )}
    </div>
  );
};

// =================================================================
// Vista principal con tabs verticales
// =================================================================

const TABS = [
  { key: 'general',   label: 'General',   icon: Sliders },
  { key: 'miembros',  label: 'Miembros',  icon: Users },
  { key: 'estados',   label: 'Estados',   icon: GitBranch },
  { key: 'etiquetas', label: 'Etiquetas', icon: Tag },
  { key: 'hitos',     label: 'Hitos',     icon: Diamond },
];

const ProyectoConfiguracionView = () => {
  const { user } = useAuth();
  const { proyecto, refetchTareas } = useOutletContext() || {};
  const [activeTab, setActiveTab] = useState('general');
  const [proyectoData, setProyectoData] = useState(proyecto);
  const navigate = useNavigate();

  useEffect(() => {
    setProyectoData(proyecto);
  }, [proyecto]);

  const refetchProyecto = useCallback(async () => {
    if (!proyecto?.slug) return;
    try {
      const { data } = await axiosInstance.get(`/api/proyectos/${proyecto.slug}`);
      setProyectoData(data);
      refetchTareas?.();
    } catch (err) {
      console.error('Error refetch proyecto:', err);
    }
  }, [proyecto?.slug, refetchTareas]);

  if (!proyectoData) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-[1200px] mx-auto px-8 pt-8 pb-12">
          <div className="flex items-center gap-2 mb-8 skeleton">
            <div className="w-4 h-4 rounded bg-slate-100" />
            <div className="h-6 w-44 bg-slate-100 rounded" />
          </div>
          <div className="grid grid-cols-[200px_1fr] gap-10 skeleton">
            <div className="flex flex-col gap-1">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-9 bg-slate-100 rounded-md" />
              ))}
            </div>
            <div>
              <div className="h-5 w-40 bg-slate-100 rounded mb-2" />
              <div className="h-3 w-72 bg-slate-100/70 rounded mb-8" />
              {[...Array(4)].map((_, i) => (
                <div key={i} className="mb-5">
                  <div className="h-3 w-16 bg-slate-100 rounded mb-2" />
                  <div className="h-9 w-full bg-slate-100/70 rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isOwner = proyectoData.mi_rol === 'owner';

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-[1200px] mx-auto px-8 pt-8 pb-12">
        <div className="flex items-center gap-2 mb-8">
          <Settings size={16} className="text-slate-400" strokeWidth={1.5} />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Configuración</h1>
          {!isOwner && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-medium rounded">
              <AlertTriangle size={9} />
              Solo los líderes pueden editar
            </span>
          )}
        </div>

        <div className="grid grid-cols-[200px_1fr] gap-10">
          {/* Sidebar tabs */}
          <nav className="flex flex-col gap-0.5 sticky top-4 self-start">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`relative flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors text-left ${
                    isActive
                      ? 'bg-cyan-50 text-cyan-700 font-medium'
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  {isActive && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] bg-cyan-500 rounded-r" />
                  )}
                  <Icon size={14} strokeWidth={isActive ? 2.2 : 1.8} />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Contenido */}
          <div>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {activeTab === 'general' && (
                  <TabGeneral proyecto={proyectoData} isOwner={isOwner} onChanged={refetchProyecto} />
                )}
                {activeTab === 'miembros' && (
                  <TabMiembros
                    proyecto={proyectoData}
                    isOwner={isOwner}
                    onChanged={refetchProyecto}
                    userEmpleadoId={user?.empleado_id}
                  />
                )}
                {activeTab === 'estados' && (
                  <TabEstados proyecto={proyectoData} isOwner={isOwner} onChanged={refetchProyecto} />
                )}
                {activeTab === 'etiquetas' && (
                  <TabEtiquetas
                    proyecto={proyectoData}
                    isOwner={isOwner}
                    onChanged={refetchProyecto}
                    userEmpleadoId={user?.empleado_id}
                  />
                )}
                {activeTab === 'hitos' && (
                  <TabHitos proyecto={proyectoData} isOwner={isOwner} onChanged={refetchProyecto} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProyectoConfiguracionView;
